<script module lang="ts">
	export type FormState = null | 'processing' | 'success' | 'error';
</script>

<script lang="ts">
	import { enhance } from '$app/forms';
	import BtnPrimary from '$lib/buttons/v2/btnPrimary.svelte';
	import type { ActionResult } from '@sveltejs/kit';
	import { type Snippet } from 'svelte';
	import { identify } from '$lib/segment/client';
	import { page } from '$app/state';
	import { event } from './utils/tracking';
	import type { ChewieUMCouponIdentifier } from './api/chewieUM.types';
	import { checkoutSession } from './state/checkoutSession.svelte';
	import { getCouponValue } from './utils/couponCode';
	import { hashEmail } from './utils/email';
	import { pushToDataLayer } from './utils/gtm';

	interface Props {
		buttonLabel?: string;
		couponIdentifier?: ChewieUMCouponIdentifier;
		couponText?: string;
		class?: string;
		legal?: Snippet;
		state?: FormState;
	}

	let {
		buttonLabel = 'Submit',
		couponIdentifier = 'NONE',
		couponText = '',
		class: className,
		legal,
		state: formState = $bindable(null)
	}: Props = $props();

	let error = $state<string | null>(null);

	const handleEnhance = () => {
		formState = 'processing';

		return async (response: {
			result: ActionResult<{
				email: string;
				couponCode?: string;
				emailVerificationResult?: string;
				couponValue?: number;
			}>;
		}) => {
			const { result } = response;

			if (result.type === 'failure') {
				error = result.data?.message || null;
				formState = 'error';
				return;
			}

			if (result.type !== 'success') {
				formState = 'error';
				return;
			}

			formState = 'success';

			if (result.data?.couponCode) {
				await checkoutSession.applyCoupon(result.data.couponCode);
			}

			if (result.data) {
				const hashedEmail = await hashEmail(result.data.email);

				pushToDataLayer({
					order: {
						email: result.data.email,
						hashed_email: hashedEmail,
						couponCode: result.data.couponCode ?? ''
					}
				});

				await identify({ email: result.data.email });
				await event('Email Captured', {
					email: result.data.email,
					hashed_email: hashedEmail,
					emailSource: 'modal',
					emailVerificationResult: result.data.emailVerificationResult,
					couponIdentifier,
					offer: couponText,
					referringPage: window.location.pathname,
					discountCode: result.data.couponCode ?? '',
					discountType: 'amount',
					discountValue:
						result.data.couponValue ??
						(checkoutSession.data.product?.promotionCode?.coupon
							? getCouponValue(checkoutSession.data.product.promotionCode.coupon)
							: 0)
				});
			}
		};
	};
</script>

<form
	class="relative email-form{` ${className}` || ''}"
	method="POST"
	action="/?/emailCapture"
	use:enhance={handleEnhance}
>
	<input type="hidden" name="couponIdentifier" value={couponIdentifier} />
	{#if couponText.length > 0}
		<input type="hidden" name="couponInfoText" value={couponText} />
	{/if}
	<input type="hidden" name="pathname" value={page.url.pathname} />
	<input
		required
		type="email"
		name="email"
		inputmode="email"
		autocomplete="email"
		placeholder="Email"
		class="h-12 w-full rounded-lg border border-stone-3 bg-stone-5 pl-3 text-[18px]/[120%] outline-none placeholder:text-brown-3 focus:border-stone-1"
	/>
	{#if formState === 'error' && error}
		<p class="mt-2 text-[12px]/[100%] text-red-500">{error}</p>
	{/if}

	{#if legal}
		<div class="mt-2 text-[12px]/[140%] text-brown-3">
			{@render legal()}
		</div>
	{/if}

	<BtnPrimary
		size="large"
		class="mt-6"
		type="submit"
		disabled={formState === 'processing'}
		title="Sign up"
	>
		{buttonLabel}
	</BtnPrimary>
</form>

<style lang="scss">
	.email-form {
		:global(a) {
			text-decoration: underline;
		}
	}
</style>
